import { fetchHangarsWithUnitsForLocation } from '@/lib/api/hangars'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { compassToSceneRotation } from '../engine/heading'
import type { AreaEligibility, PlacementConfig } from '@/types/area-placement'
import type {
  ZoneGeometry,
  PlacementPattern,
  PlacementProgression,
  LaneCoupling,
  PitchMode,
} from '../engine/types'

const num = (v: unknown): number => Number(v) || 0
/** Positive number or undefined (so optional caps stay unset rather than 0). */
const posOrUndef = (v: unknown): number | undefined => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

/** Outdoor placement categories the planner fills. */
const PLACEABLE_CATEGORIES = new Set(['ramp', 'tiedown'])

/** Fill order: ramps first (closest service), then intermediate, then long-term. */
function priorityFor(category: string | null, name: string): number {
  if (category === 'ramp') return 10
  if (category === 'tiedown') return /long\s*term/i.test(name) ? 30 : 20
  return 40
}

/**
 * Resolve KAGS authored areas into placement-ready ZoneGeometry.
 *
 * KAGS already has real ramp/tiedown areas (geometry + rotation + extents) but
 * their placement-authoring fields are still null, so we fill sensible defaults
 * here. If no placeable areas exist, a single synthetic apron is returned so the
 * demo is always runnable.
 */
export async function loadZoneGeometry(
  locationId: string,
  mapCenter: { lat: number; lng: number }
): Promise<ZoneGeometry[]> {
  const areas = await fetchHangarsWithUnitsForLocation(locationId)

  const zones: ZoneGeometry[] = []

  for (const area of areas) {
    const category = area.area_category ?? null
    if (!PLACEABLE_CATEGORIES.has(category ?? '')) continue

    const width = num(area.exterior_width)
    const depth = num(area.exterior_depth)
    if (width <= 0 || depth <= 0) continue

    const [x, , z] = CoordinateService.lngLatToScenePosition(
      [num(area.longitude), num(area.latitude)],
      [mapCenter.lng, mapCenter.lat]
    )

    // Defaults for the still-null authoring fields.
    const lanes = area.lanes ?? (depth >= 140 ? 2 : 1)
    const pattern: PlacementPattern =
      (area.pattern as PlacementPattern | null) ?? (lanes >= 2 ? 'double_row' : 'row')
    const coupling: LaneCoupling | null =
      (area.coupling as LaneCoupling | null) ?? (lanes >= 2 ? 'same_direction' : null)

    // Authored size caps double as the designated fixed-slot box for uniform pitch.
    const size = (area.eligibility as AreaEligibility | null)?.size
    const patternParams = (area.placement_config as PlacementConfig | null)?.pattern_params

    zones.push({
      areaId: area.id,
      name: area.name,
      code: area.area_code ?? area.name,
      center: [x, z],
      rotationDeg: num(area.rotation),
      width,
      depth,
      pattern,
      progression: (area.progression as PlacementProgression | null) ?? 'side_by_side',
      coupling,
      lanes,
      safetyMarginFt: area.safety_margin_ft ?? 15,
      // `placement_heading` is authored as a TRUE compass bearing (0 = north).
      // Convert to the scene Y-rotation the engine applies directly. The
      // `rotation` fallback is already a scene rotation, so it's used as-is.
      placementHeadingDeg:
        area.placement_heading != null
          ? compassToSceneRotation(area.placement_heading)
          : num(area.rotation),
      pitchMode: (area.pitch_mode as PitchMode | null) ?? 'variable',
      tailWidthRatio: posOrUndef(patternParams?.tail_width_ratio),
      footprintRows: patternParams?.footprint_rows ?? false,
      fillPriority: area.fill_priority ?? priorityFor(category, area.name),
      maxWingspanFt: posOrUndef(size?.max_wingspan_ft),
      maxLengthFt: posOrUndef(size?.max_length_ft),
      // Apron-taxilane access policy. Defaults to 'auto' (connector count scaled
      // to perimeter length); ready to read from an area authoring column/UI
      // later. Per-area override of the network's apron→taxiway connection.
      connectorPolicy: 'auto',
      // Authored tow/self-park policy (areas.parking_mode). Undefined falls back
      // to the motion config's per-area / default policy in resolveProcedure.
      parkingMode: (area.parking_mode as ZoneGeometry['parkingMode']) ?? undefined,
      towMinWingspanFt: posOrUndef(area.tow_min_wingspan_ft),
    })
  }

  if (zones.length === 0) {
    // Synthetic fallback apron centered on the map origin.
    zones.push({
      areaId: 'synthetic-apron',
      name: 'Event Apron',
      code: 'APRON',
      center: [0, 0],
      rotationDeg: 0,
      width: 600,
      depth: 300,
      pattern: 'double_row',
      progression: 'side_by_side',
      coupling: 'same_direction',
      lanes: 2,
      safetyMarginFt: 15,
      placementHeadingDeg: 0,
      pitchMode: 'variable',
      fillPriority: 10,
    })
  }

  zones.sort((a, b) => a.fillPriority - b.fillPriority)
  return zones
}
